import {
  ActionIcon,
  AspectRatio,
  Box,
  Center,
  Flex,
  Group,
  Image,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import type { AttachmentResponse } from '@markku/types'
import { partition } from '@markku/utils'
import {
  IconFileTypePdf,
  IconPencil,
  IconPlayerPlay,
  IconRefresh,
} from '@tabler/icons-react'

import { type ReactNode, useEffect, useState } from 'react'
import { validateFileAge } from '../utils/fileValidation'
import { compressImage } from '../utils/image'
import { AddPhotoButton } from './AddPhotoButton'
import { AttachmentFileLinks } from './AttachmentFileLinks'
import { PhotoPreviewModal } from './PhotoPreviewModal'

// Support both AttachmentResponse and simple photo types
type SimplePhoto = {
  id: number
  downloadUrl?: string | null
  mimeType?: string
}

// Type guard to check if photo is AttachmentResponse
function isAttachmentResponse(
  photo: AttachmentResponse | SimplePhoto,
): photo is AttachmentResponse {
  return 'filename' in photo
}

// Helper to check if media is a video
function isVideo(photo: AttachmentResponse | SimplePhoto): boolean {
  if (isAttachmentResponse(photo)) {
    return photo.mimeType?.startsWith('video/') ?? false
  }
  return photo.mimeType?.startsWith('video/') ?? false
}

// Anything else (a PDF plan from the pre-info form) can't render in <img>:
// Android shows it broken, iOS rasterises page one onto a transparent
// background. It is listed as a link for the phone's own viewer instead.
function isMedia(photo: AttachmentResponse | SimplePhoto): boolean {
  return !photo.mimeType || /^(image|video)\//.test(photo.mimeType)
}

function VideoPlaceholder({
  onClick,
  style,
}: {
  onClick?: () => void
  style?: React.CSSProperties
}) {
  return (
    <Box
      onClick={onClick}
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'var(--mantine-color-dark-6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <IconPlayerPlay size={32} color="white" />
    </Box>
  )
}

// Optimistic, not-yet-uploaded media shown alongside server photos in capture mode
export interface PendingMediaItem {
  id: string
  previewUrl: string
  isVideo: boolean
  isPdf?: boolean
  status: 'uploading' | 'failed' | 'done'
  // Set once uploaded; lets us hide the server twin until its image has loaded.
  attachmentId?: number
}

export interface PhotoGalleryProps {
  photos: AttachmentResponse[] | SimplePhoto[]
  label?: string
  compact?: boolean
  cols?: number
  onUpdate?: (id: number, description: string) => Promise<void>
  onDelete?: (id: number) => void | Promise<void>
  // Capture mode props
  onCapture?: (file: File) => void
  onCaptureMultiple?: (files: File[]) => void
  pendingItems?: PendingMediaItem[]
  onRetryPending?: (id: string) => void
  disabled?: boolean
  enforceMaxAge?: boolean
  showAddButton?: boolean
  allowVideo?: boolean
  /** PDFs are listed as file links below the thumbnails, not as thumbnails. */
  allowPdf?: boolean
  /** Drawn over a saved thumbnail's corner in capture mode. */
  renderThumbnailAction?: (photo: AttachmentResponse) => ReactNode
}

function PendingThumbnail({
  item,
  thumbnailSize,
  onRetry,
  onOpen,
}: {
  item: PendingMediaItem
  thumbnailSize: { width: number | string; height: number }
  onRetry?: (id: string) => void
  onOpen?: () => void
}) {
  return (
    <Box
      onClick={onOpen}
      style={{
        position: 'relative',
        ...thumbnailSize,
        borderRadius: 8,
        overflow: 'hidden',
        cursor: onOpen ? 'pointer' : undefined,
      }}
    >
      {item.isVideo ? (
        <VideoPlaceholder />
      ) : item.isPdf ? (
        <Center h="100%" bg="gray.1">
          <IconFileTypePdf size={32} />
        </Center>
      ) : (
        <Image
          src={item.previewUrl}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}

      {/* 'done' shows no overlay: the preview is held with a clean thumbnail
          until the real server photo replaces it, avoiding a flicker. */}
      {item.status === 'uploading' && (
        <Center
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
          }}
        >
          <Loader size="sm" color="white" />
        </Center>
      )}

      {item.status === 'failed' && (
        <Stack
          gap={4}
          align="center"
          justify="center"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
          }}
        >
          <Text size="xs" c="white" fw={600}>
            Epäonnistui
          </Text>
          {onRetry && (
            <ActionIcon
              variant="white"
              size="sm"
              radius="xl"
              aria-label="Yritä uudelleen"
              onClick={(e) => {
                e.stopPropagation()
                onRetry(item.id)
              }}
            >
              <IconRefresh size={16} />
            </ActionIcon>
          )}
        </Stack>
      )}
    </Box>
  )
}

function InlineDescription({
  photo,
  onUpdate,
  disabled,
}: {
  photo: AttachmentResponse | SimplePhoto
  onUpdate?: (id: number, description: string) => Promise<void>
  disabled?: boolean
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [pendingValue, setPendingValue] = useState<string | null>(null)

  const isAttachment = isAttachmentResponse(photo)
  const description = isAttachment ? photo.description || '' : ''

  // Clear pending value when the server description catches up
  useEffect(() => {
    if (pendingValue !== null && description === pendingValue) {
      setPendingValue(null)
    }
  }, [description, pendingValue])

  if (!isAttachment) return null

  const displayValue = pendingValue ?? description

  // Read-only: no onUpdate handler or disabled
  if (!onUpdate || disabled) {
    if (!displayValue) return null
    return (
      <Text size="xs" c="dimmed" lineClamp={1}>
        {displayValue}
      </Text>
    )
  }

  if (isEditing) {
    return (
      // Never drop below md: iOS zooms the viewport when focusing an input
      // under 16px and never zooms back out, which strands the user in a
      // zoomed view where full-screen overlays have no reachable controls.
      <TextInput
        size="md"
        value={editValue}
        onChange={(e) => setEditValue(e.currentTarget.value)}
        onBlur={() => {
          setIsEditing(false)
          if (editValue !== description) {
            setPendingValue(editValue)
            onUpdate(photo.id, editValue)
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') setIsEditing(false)
        }}
        placeholder="Kuvaus..."
        autoFocus
        onClick={(e) => e.stopPropagation()}
      />
    )
  }

  return (
    <UnstyledButton
      onClick={() => {
        setEditValue(displayValue)
        setIsEditing(true)
      }}
      // Gloved field use: keep the row at a full touch target so a miss lands
      // here rather than on the thumbnail above it.
      style={{
        width: '100%',
        minHeight: 44,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Group gap={6} wrap="nowrap">
        <IconPencil
          size={16}
          color="var(--mantine-color-dimmed)"
          style={{ flexShrink: 0 }}
        />
        <Text size="sm" c={displayValue ? undefined : 'dimmed'} lineClamp={1}>
          {displayValue || 'Kuvaus...'}
        </Text>
      </Group>
    </UnstyledButton>
  )
}

export function PhotoGallery({
  photos: attachments,
  label,
  compact = false,
  cols,
  onUpdate,
  onDelete,
  onCapture,
  onCaptureMultiple,
  pendingItems = [],
  onRetryPending,
  disabled = false,
  enforceMaxAge = true,
  showAddButton = true,
  allowVideo = false,
  allowPdf = false,
  renderThumbnailAction,
}: PhotoGalleryProps) {
  const [previewOpened, { open: openPreview, close: closePreview }] =
    useDisclosure(false)
  const [previewIndex, setPreviewIndex] = useState(0)
  // Optimistic previews aren't server photos yet, so they get their own
  // read-only full-screen view (the same modal, fed the local object URL). This
  // makes a just-captured photo openable instantly instead of dead until upload.
  const [pendingPreviewId, setPendingPreviewId] = useState<string | null>(null)
  const pendingPreviewItem = pendingItems.find(
    (item) => item.id === pendingPreviewId,
  )

  // Determine if we're in capture mode
  const isCaptureMode = !!onCapture

  // Return null for display mode when no photos
  if (!isCaptureMode && attachments.length === 0) {
    return null
  }

  const [photos, files] = partition<AttachmentResponse | SimplePhoto>(
    attachments,
    isMedia,
  )

  const getMaxAgeHours = () =>
    enforceMaxAge ? Number(import.meta.env.VITE_MAX_PHOTO_AGE_HOURS) : undefined

  const prepareFile = async (file: File): Promise<File | null> => {
    const isImage = file.type.startsWith('image/')
    const isVideoFile = file.type.startsWith('video/')
    const isPdf = file.type === 'application/pdf'
    if (!isImage && !(allowVideo && isVideoFile) && !(allowPdf && isPdf))
      return null
    return isImage ? compressImage(file) : file
  }

  const handleFileSelect = async (file: File) => {
    if (!onCapture) return
    if (file.type.startsWith('image/')) {
      if (!(await validateFileAge(file, getMaxAgeHours()))) return
    }
    const prepared = await prepareFile(file)
    if (prepared) onCapture(prepared)
  }

  const handleMultipleFilesSelect = async (files: File[]) => {
    if (!onCapture && !onCaptureMultiple) return
    const maxAgeHours = getMaxAgeHours()
    if (maxAgeHours !== undefined) {
      const oldestFile = files.reduce((oldest, file) =>
        file.lastModified < oldest.lastModified ? file : oldest,
      )
      if (!(await validateFileAge(oldestFile, maxAgeHours))) return
    }
    const prepared = (await Promise.all(files.map(prepareFile))).filter(
      (f): f is File => f !== null,
    )
    if (!prepared.length) return
    if (onCaptureMultiple) {
      onCaptureMultiple(prepared)
    } else {
      prepared.forEach((f) => onCapture!(f))
    }
  }

  // Preview handlers - unified for both modes
  const handleThumbnailClick = (index: number) => {
    setPreviewIndex(index)
    openPreview()
  }

  // Render capture mode
  if (isCaptureMode) {
    const thumbnailSize = compact
      ? { width: '100%', height: 80 }
      : { width: 100, height: 100 }
    // Hide a server photo while its optimistic preview is still held (the held
    // preview shows it in the same slot until the real image has decoded), so
    // the swap doesn't briefly render two copies of the same photo.
    const heldAttachmentIds = new Set(
      pendingItems
        .filter((item) => item.status === 'done' && item.attachmentId != null)
        .map((item) => item.attachmentId),
    )
    const visiblePhotos = photos.filter(
      (photo) => !heldAttachmentIds.has(photo.id),
    )
    const displayPhotos = compact ? visiblePhotos.slice(0, 1) : visiblePhotos
    const hasMedia = visiblePhotos.length > 0 || pendingItems.length > 0

    const Container = compact ? Stack : Flex
    const containerProps = compact
      ? { gap: 4 }
      : { gap: 'sm', wrap: 'wrap' as const }

    return (
      <>
        <Stack gap={4}>
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text size="sm" fw={500} c="dimmed">
              {label}
            </Text>

            {!disabled && hasMedia && showAddButton && (
              <AddPhotoButton
                onFileSelect={handleFileSelect}
                onMultipleFilesSelect={handleMultipleFilesSelect}
                variant="subtle"
                allowVideo={allowVideo}
                allowPdf={allowPdf}
              />
            )}
          </Group>

          <Container {...containerProps}>
            {displayPhotos.map((photo, index) => (
              <Stack
                key={photo.id}
                gap={6}
                style={compact ? undefined : { width: 100 }}
              >
                <Box
                  style={{
                    position: 'relative',
                    ...thumbnailSize,
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  <UnstyledButton
                    onClick={() => handleThumbnailClick(index)}
                    disabled={disabled}
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                    }}
                  >
                    {isVideo(photo) ? (
                      <VideoPlaceholder />
                    ) : (
                      <Image
                        src={photo.downloadUrl}
                        alt={`${label} ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </UnstyledButton>
                  {renderThumbnailAction &&
                    !disabled &&
                    isAttachmentResponse(photo) && (
                      <Box pos="absolute" top={4} right={4}>
                        {renderThumbnailAction(photo)}
                      </Box>
                    )}
                </Box>
                <InlineDescription
                  photo={photo}
                  onUpdate={onUpdate}
                  disabled={disabled}
                />
              </Stack>
            ))}

            {pendingItems.map((item) => (
              <PendingThumbnail
                key={item.id}
                item={item}
                thumbnailSize={thumbnailSize}
                onRetry={onRetryPending}
                onOpen={() => setPendingPreviewId(item.id)}
              />
            ))}

            {!hasMedia && !disabled && (
              <AddPhotoButton
                onFileSelect={handleFileSelect}
                onMultipleFilesSelect={handleMultipleFilesSelect}
                size="compact-md"
                variant="light"
                allowVideo={allowVideo}
                allowPdf={allowPdf}
              />
            )}
          </Container>

          <AttachmentFileLinks
            files={files}
            onDelete={
              onDelete && !disabled ? (file) => onDelete(file.id) : undefined
            }
          />
        </Stack>

        {/* Preview modal for capture mode */}
        <PhotoPreviewModal
          photos={visiblePhotos}
          initialIndex={previewIndex}
          opened={previewOpened}
          onClose={closePreview}
          onDelete={onDelete}
          onUpdate={onUpdate}
          disabled={disabled}
          label={label}
        />

        {/* Read-only full-screen view of an optimistic preview, served from the
            local object URL so it opens instantly while the upload is in flight.
            No onDelete/onUpdate: a not-yet-uploaded item has no server id. */}
        <PhotoPreviewModal
          photos={
            pendingPreviewItem
              ? [
                  {
                    id: -1,
                    downloadUrl: pendingPreviewItem.previewUrl,
                    mimeType: pendingPreviewItem.isVideo
                      ? 'video/mp4'
                      : 'image/*',
                  },
                ]
              : []
          }
          initialIndex={0}
          opened={!!pendingPreviewItem}
          onClose={() => setPendingPreviewId(null)}
          label={label}
        />
      </>
    )
  }

  // Render display mode
  return (
    <>
      <Box>
        {label && (
          <Text size="sm" fw={500} mb="xs">
            {label}
          </Text>
        )}
        <SimpleGrid cols={cols ?? 4} spacing={compact ? 'xs' : 'sm'}>
          {photos.map((photo, index) => (
            <Stack key={photo.id} gap={4}>
              <AspectRatio ratio={1}>
                {isVideo(photo) ? (
                  <VideoPlaceholder
                    onClick={() => handleThumbnailClick(index)}
                    style={{
                      cursor: 'pointer',
                      borderRadius: 'var(--mantine-radius-sm)',
                    }}
                  />
                ) : (
                  <Image
                    src={photo.downloadUrl}
                    alt={
                      isAttachmentResponse(photo)
                        ? photo.description || photo.filename
                        : `Photo ${index + 1}`
                    }
                    radius="sm"
                    fit="cover"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleThumbnailClick(index)}
                  />
                )}
              </AspectRatio>
              <InlineDescription photo={photo} onUpdate={onUpdate} />
            </Stack>
          ))}
        </SimpleGrid>
        <AttachmentFileLinks files={files} />
      </Box>

      {/* Preview modal for display mode */}
      <PhotoPreviewModal
        photos={photos}
        initialIndex={previewIndex}
        opened={previewOpened}
        onClose={closePreview}
        onDelete={onDelete}
        label={label}
        onUpdate={onUpdate}
        showConfirmation={true}
      />
    </>
  )
}
